import { DownOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space } from 'antd';
import type { ButtonProps } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getPrintSize } from './print-size';
import type { PrintDoc, PrintSize } from './print-types';
import { usePrint } from './usePrint';

const SIZES: PrintSize[] = ['80mm', 'a4'];

export interface PrintButtonProps {
  doc: PrintDoc;
  uid: string;
  label: string;
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
  /** Shows only the printer icon (the label still names it for screen readers). For dense rows. */
  iconOnly?: boolean;
}

/**
 * Prints a document. The main click uses the size this device printed last
 * (or the default); the menu picks "80mm slip" or "A4 page", prints at it, and
 * remembers it. Shows a spinner until the print dialog opens.
 */
export function PrintButton({ doc, uid, label, type, size = 'large', iconOnly = false }: PrintButtonProps): ReactNode {
  const { t } = useTranslation();
  const { print, printing } = usePrint();

  // Touch targets: never shorter than 32px, even in the small icon-only mode.
  const touch = { minHeight: 32, minWidth: 32 };

  const run = (chosen: PrintSize): void => {
    void print(doc, uid, chosen);
  };

  return (
    <Space.Compact>
      <Button
        type={type}
        size={size}
        style={touch}
        icon={<PrinterOutlined />}
        loading={printing}
        aria-label={label}
        onClick={() => {
          run(getPrintSize(doc));
        }}
      >
        {iconOnly ? null : label}
      </Button>
      <Dropdown
        trigger={['click']}
        disabled={printing}
        menu={{
          items: SIZES.map((s) => ({ key: s, label: t(`print.size.${s}`) })),
          onClick: ({ key }) => {
            run(key === 'a4' ? 'a4' : '80mm');
          },
        }}
      >
        <Button type={type} size={size} style={touch} aria-label={t('print.chooseSize')} disabled={printing}>
          <DownOutlined />
        </Button>
      </Dropdown>
    </Space.Compact>
  );
}
